import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import MainHeader from '../components/MainHeader';
import MainFooter from '../components/MainFooter';

function FreeTools() {
  const tools = [
    {
      name: 'AI Accountability Score',
      description: 'Get a scored assessment of your organization\'s AI accountability posture.',
      delivers: [
        'Your AI accountability score (0–100)',
        'Risk tier classification',
        '3–5 tier-specific recommendations'
      ],
      doesNotInclude: 'Does not include policy documents, templates, or a governance summary report.',
      cta: 'Get Your Score',
      link: '/assessment/form'
    },
    {
      name: 'AI Policy Generator',
      description: 'Generate a basic AI Acceptable Use Policy customized for your organization.',
      delivers: [
        'One AI Acceptable Use Policy document',
        'Customized with your organization name and industry',
        'Downloadable PDF format'
      ],
      doesNotInclude: 'Does not include inventory, risk register, incident log, or other governance templates.',
      cta: 'Generate Your Policy',
      link: '/tools/policy-generator'
    },
    {
      name: 'AI Tool Inventory Builder',
      description: 'Document every AI tool your organization uses in a structured, downloadable register.',
      delivers: [
        'A structured register of up to 10 AI tools',
        'Unapproved tools flagged and highlighted',
        'Downloadable PDF format'
      ],
      doesNotInclude: 'Does not include policies, risk assessments, or vendor scoring.',
      cta: 'Build Your Inventory',
      link: '/tools/inventory-builder'
    },
    {
      name: 'AI Governance Checklist',
      description: 'Answer 12 questions and get a gap report showing exactly what your organization is missing.',
      delivers: [
        'A score out of 24 across 12 governance areas',
        'Gap report showing which areas need attention',
        'Prioritized top-3 action list'
      ],
      doesNotInclude: 'Does not include any templates needed to close the gaps it identifies.',
      cta: 'Get Your Checklist',
      link: '/tools/governance-checklist'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <MainHeader />
      
      <main className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-16">
            <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4">
              FREE TOOLS
            </p>
            <h1 className="text-[40px] font-bold text-[#102A43] mb-4 leading-tight">
              Free AI Governance Tools
            </h1>
            <p className="text-[18px] text-[#64748B] max-w-2xl mx-auto">
              Four tools. Each one surfaces a governance gap. Free — no signup until you want your results.
            </p>
          </div>

          {/* Tool Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {tools.map((tool, index) => (
              <div key={tool.name} data-testid={`free-tool-card-${index}`} className="bg-white border border-[#E2E8F0] rounded-[10px] p-6">
                <h2 className="text-[20px] font-bold text-[#102A43] mb-2">{tool.name}</h2>
                <p className="text-[14px] text-[#64748B] mb-4">{tool.description}</p>
                
                <div className="mb-4">
                  <p className="text-[11px] font-bold text-[#15803D] uppercase tracking-wide mb-2">
                    What you get:
                  </p>
                  <ul className="space-y-1">
                    {tool.delivers.map((item, i) => (
                      <li key={i} className="text-[13px] text-[#102A43] flex items-start gap-2">
                        <span className="text-[#15803D]">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-[12px] text-[#94A3B8] mb-6">{tool.doesNotInclude}</p>

                <Link 
                  to={tool.link}
                  data-testid={`free-tool-cta-${index}`}
                  className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-5 py-2 rounded-[7px] hover:bg-[#2563eb] transition-colors text-[14px]"
                >
                  {tool.cta}
                  <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>

          {/* Upsell CTA */}
          <div className="bg-[#102A43] rounded-[10px] p-8 text-center">
            <h2 className="text-[24px] font-bold text-white mb-3">
              Close every gap these tools surface
            </h2>
            <p className="text-[16px] text-[#94A3B8] mb-6">
              The AI Accountability Documentation System includes all eight templates. $97 one-time.
            </p>
            <Link 
              to="/documentation-system"
              className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors"
            >
              Get the Documentation System
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </main>

      <MainFooter />
    </div>
  );
}

export default FreeTools;
